import { useState } from 'react'
import { apiClient, ApiClientError } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type FieldErr = { current: string | null; new: string | null; confirm: string | null }

/**
 * U4SJh 修改密码页 — frame U4SJh @ ui/dogdisk-user.pen
 *
 * 入口:uOqAt 设置 hub menuItem7 (f3LDq) — uOqAt 暂未建,本页可直接通过 URL 访问。
 * 后端:POST /gapi/user/auth/change-password(已实装;body {current_password, new_password}
 *      错误码 CURRENT_PASSWORD_INVALID / WEAK_PASSWORD;成功后强制注销其他 session,
 *      本机 token 保留)。
 *
 * Spec:docs/superpowers/specs/2026-05-26-U4SJh-change-password-design.md
 */
export function ChangePasswordPage() {
  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [busy, setBusy] = useState(false)
  const [fieldErr, setFieldErr] = useState<FieldErr>({ current: null, new: null, confirm: null })
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [errorBanner, setErrorBanner] = useState<string | null>(null)

  const validateForm = ():
    | { ok: true }
    | { ok: false; field: 'current' | 'new' | 'confirm'; msg: string } => {
    if (!currentPwd) return { ok: false, field: 'current', msg: '请输入当前密码' }
    if (newPwd.length < 8) return { ok: false, field: 'new', msg: '新密码至少 8 个字符' }
    if (!/[a-zA-Z]/.test(newPwd)) return { ok: false, field: 'new', msg: '新密码需包含字母' }
    if (!/[0-9]/.test(newPwd)) return { ok: false, field: 'new', msg: '新密码需包含数字' }
    if (newPwd === currentPwd) return { ok: false, field: 'new', msg: '新密码不能与当前密码相同' }
    if (newPwd !== confirmPwd) return { ok: false, field: 'confirm', msg: '两次输入不一致' }
    return { ok: true }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFieldErr({ current: null, new: null, confirm: null })
    setSuccessMsg(null)
    setErrorBanner(null)

    const v = validateForm()
    if (!v.ok) {
      setFieldErr((prev) => ({ ...prev, [v.field]: v.msg }))
      return
    }

    setBusy(true)
    try {
      await apiClient.post('/gapi/user/auth/change-password', {
        current_password: currentPwd,
        new_password: newPwd,
      })
      setCurrentPwd('')
      setNewPwd('')
      setConfirmPwd('')
      setSuccessMsg('密码已更新')
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'CURRENT_PASSWORD_INVALID') {
        setFieldErr((prev) => ({ ...prev, current: '当前密码不正确' }))
      } else if (err instanceof ApiClientError && err.code === 'WEAK_PASSWORD') {
        setFieldErr((prev) => ({ ...prev, new: '新密码至少 8 个字符' }))
      } else {
        setErrorBanner('网络错误,请重试')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <MobileScreen>
      <TopBar />
      <main data-testid="U4SJh" className="flex flex-col gap-6 px-6 pt-2">
        <h1 className="text-[26px] font-bold text-[#1A1A1A]">修改密码</h1>

        {successMsg && (
          <div className="rounded-lg bg-[#ebfaf6] p-4 text-[13px] text-[#0f7969]">{successMsg}</div>
        )}
        {errorBanner && (
          <div className="rounded-lg bg-[#fdecec] p-4 text-[13px] text-[#b3261e]">{errorBanner}</div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">当前密码</span>
            <input
              data-testid="mkPut"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              placeholder="请输入当前密码"
              autoComplete="current-password"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
            />
            {fieldErr.current && <p className="text-[13px] text-[#FF6B6B]">{fieldErr.current}</p>}
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">新密码</span>
            <input
              data-testid="WO0T2"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="请输入新密码"
              autoComplete="new-password"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
            />
            {fieldErr.new && <p className="text-[13px] text-[#FF6B6B]">{fieldErr.new}</p>}
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">确认新密码</span>
            <input
              data-testid="EiPOc"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="请再次输入新密码"
              autoComplete="new-password"
              className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
            />
            {fieldErr.confirm && <p className="text-[13px] text-[#FF6B6B]">{fieldErr.confirm}</p>}
          </label>

          <ul className="flex flex-col gap-1 text-[13px] text-[#9CA3AF]">
            <li>⏷ 至少 8 个字符</li>
            <li>⏷ 包含字母和数字</li>
            <li>⏷ 与当前密码不同</li>
          </ul>

          <button
            type="submit"
            data-testid="nMdyq"
            disabled={busy}
            className="mt-2 h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '提交中…' : '保存'}
          </button>
        </form>
      </main>
    </MobileScreen>
  )
}
